import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { getTreasuryBalance, fetchOnChainClaimableFees } from '@/lib/solana'

const LAMPORTS_PER_SOL = 1_000_000_000

export interface TreasuryData {
  claimableFeesSOL: number     // SOL waiting to be claimed from pools (on-chain)
  treasuryBalanceSOL: number   // On-chain SOL balance of treasury wallet
  claimedFeesSOL: number       // Historical total of successfully claimed fees
  poolsWithFees: number        // Number of pools with claimable partner fees above dust threshold
  lastUpdated: Date
}

/**
 * Hook to fetch claimable protocol fees directly from on-chain pool accounts,
 * on-chain treasury balance, and historical claimed totals.
 */
export function useTreasuryData() {
  return useQuery({
    queryKey: ['treasury-data'],
    queryFn: async (): Promise<TreasuryData> => {
      // 1. Get pool addresses from launched_tokens
      const poolsResult = await supabase
        .from('launched_tokens')
        .select('pool_address')
        .not('pool_address', 'is', null)

      const poolAddresses: string[] = []
      if (poolsResult.error) {
        console.error('[Treasury] launched_tokens query error:', poolsResult.error)
      } else if (poolsResult.data) {
        for (const row of poolsResult.data) {
          if (row.pool_address) poolAddresses.push(row.pool_address)
        }
      }

      // 2. Fetch on-chain claimable fees, treasury balance, and claimed history in parallel
      const [onChainFees, treasuryBalance, claimedResult] = await Promise.all([
        poolAddresses.length > 0
          ? fetchOnChainClaimableFees(poolAddresses)
          : Promise.resolve({ totalLamports: 0, claimablePoolCount: 0 }),

        getTreasuryBalance(),

        supabase
          .from('admin_fee_claims')
          .select('amount_lamports_quote, status')
      ])

      // Sum historical claimed fees (successful claims only)
      let claimedLamports = 0

      if (claimedResult.error) {
        console.error('[Treasury] admin_fee_claims query error:', claimedResult.error)
      } else if (claimedResult.data) {
        for (const row of claimedResult.data) {
          if (row.status === 'confirmed' || row.status === 'success') {
            claimedLamports += Number(row.amount_lamports_quote) || 0
          }
        }
      }

      return {
        claimableFeesSOL: onChainFees.totalLamports / LAMPORTS_PER_SOL,
        treasuryBalanceSOL: treasuryBalance,
        claimedFeesSOL: claimedLamports / LAMPORTS_PER_SOL,
        poolsWithFees: onChainFees.claimablePoolCount,
        lastUpdated: new Date(),
      }
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}
